/**
 * THE EDITOR'S SCHEMA - exactly the formatting the platform stores, and nothing it would have to
 * refuse or drop.
 *
 * The article body is not HTML: a text block is plain text with PARAGRAPH STYLES and INLINE MARKS
 * in rows beside it (`authors-api.yaml`'s `TextBlock`, `Paragraph`, `InlineMark`), and the seeded
 * vocabularies are seven styles - `p`, `h1`..`h4`, `ul`, `ol` - and six marks - strong, em,
 * underline, strike, code, link. So the starter kit is cut down to that, `h1` aside (below):
 *
 *   ON   paragraph, heading 2..4, bullet list, ordered list (+ list item and its keymap), bold,
 *        italic, underline, strike, code, link, and the kit's undo history, drop and gap cursors.
 *   OFF  blockquote (a quotation is a quote BLOCK, not a paragraph style - `BlockKind`'s own
 *        description), code block, horizontal rule (a break is a divider BLOCK), hard break (a line
 *        break inside a paragraph has no place in `body`, where U+000A separates paragraphs), and
 *        the trailing node - it appends an empty paragraph after a closing heading or list on the
 *        first keystroke, which would change the author's text without the author.
 *
 * ★ A LINK IS ON ONLY WHEN THE CALLER SAYS WHICH ADDRESSES ARE STORED. The platform's rule is a
 * database constraint (`ck_awmark_href`, `^(https?://|mailto:)`) and lives in `shared/content-model`
 * as `LINK_HREF_RE`; this package does not import the content model, so the screen passes the test
 * in (`isAllowedLink`) rather than this file keeping a second copy of it. Every way a link can
 * appear - the toolbar, typing an address (autolink), pasting one, pasting HTML - goes through it.
 * `defaultProtocol` is `https` so a typed `moonx.art` becomes `https://moonx.art` and not `http://`.
 *
 * The node and mark NAMES are the kit's defaults, and they are the names
 * `shared/content-model/editor-json.ts` builds and reads (`EDITOR_NODE`, `EDITOR_MARK_OF`);
 * `web/ui/test/components/Editor/rich-text.spec.tsx` loads that converter's documents into this
 * schema to prove it.
 */
import StarterKit from '@tiptap/starter-kit';
import type { AnyExtension } from '@tiptap/react';

/** Not h1: the title is the page's only H1 (design 24 T3, 2026-10-11); a stored h1 still opens. */
export const HEADING_LEVELS = [2, 3, 4] as const;
export type HeadingLevel = (typeof HEADING_LEVELS)[number];

export interface EditorSchemaOptions {
  /** Headings and lists. Off for a quotation, whose text has no paragraph styles. */
  readonly paragraphStyles: boolean;
  /** Which link addresses may be stored. Absent: no links at all. */
  readonly isAllowedLink: ((href: string) => boolean) | undefined;
}

export function editorExtensions({
  paragraphStyles,
  isAllowedLink,
}: EditorSchemaOptions): AnyExtension[] {
  return [
    StarterKit.configure({
      blockquote: false,
      codeBlock: false,
      horizontalRule: false,
      hardBreak: false,
      trailingNode: false,
      heading: paragraphStyles ? { levels: [...HEADING_LEVELS] } : false,
      bulletList: paragraphStyles ? {} : false,
      orderedList: paragraphStyles ? {} : false,
      listItem: paragraphStyles ? {} : false,
      listKeymap: paragraphStyles ? {} : false,
      link:
        isAllowedLink === undefined
          ? false
          : {
              openOnClick: false,
              autolink: true,
              linkOnPaste: true,
              defaultProtocol: 'https',
              isAllowedUri: (url) => isAllowedLink(url),
              shouldAutoLink: (url) => isAllowedLink(url),
            },
    }),
  ];
}
